import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { adminAPI } from '../services/api';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Mail, 
  Calendar,
  MessageSquare,
  User,
  RefreshCw,
  Filter
} from 'lucide-react';

interface AccessRequest {
  _id: string;
  email: string;
  name: string;
  message: string;
  status: 'pending' | 'approved' | 'rejected';
  requested_at: string;
  processed_at?: string;
  processed_by?: string;
  admin_notes?: string;
}

export const AccessRequestsPanel: React.FC = () => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<AccessRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState<'approved' | 'rejected'>('approved');

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const response = await adminAPI.getAccessRequests(statusFilter || undefined);
      if (response.status === 'success') {
        setRequests(response.requests);
      } else {
        toast.error('Failed to fetch access requests');
      }
    } catch (error: any) {
      console.error('Error fetching requests:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
      } else {
        toast.error('Failed to fetch access requests');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessRequest = async (requestId: string, action: 'approved' | 'rejected', notes: string = '') => {
    try {
      setProcessingRequest(requestId);
      const response = await adminAPI.processAccessRequest(requestId, action, notes);
      
      if (response.status === 'success') {
        toast.success(response.message);
        fetchRequests(); // Refresh the list
        setShowModal(false);
        setSelectedRequest(null);
        setAdminNotes('');
      } else {
        toast.error(response.message);
      }
    } catch (error: any) {
      console.error('Error processing request:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to process request');
      }
    } finally {
      setProcessingRequest(null);
    }
  };

  const openProcessModal = (request: AccessRequest, action: 'approved' | 'rejected') => {
    setSelectedRequest(request);
    setModalAction(action);
    setAdminNotes('');
    setShowModal(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-orange-600 p-2 rounded-lg">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Access Requests</h2>
            <p className="text-sm text-gray-600">
              Manage user access requests {pendingCount > 0 && `(${pendingCount} pending)`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-md px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">All Requests</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <Filter className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={fetchRequests}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Requests List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading requests...</p>
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            {statusFilter ? `No ${statusFilter} requests found` : 'No access requests found'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div
              key={request._id}
              className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-gray-100 rounded-full p-2">
                      <User className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{request.name}</h3>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {request.email}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                      <span className="ml-1 capitalize">{request.status}</span>
                    </span>
                  </div>

                  <div className="ml-11 space-y-2">
                    <div className="text-sm text-gray-600 flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      Requested on {formatDate(request.requested_at)}
                    </div>
                    
                    {request.message && (
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-sm text-gray-700">
                          <strong>Message:</strong> {request.message}
                        </p>
                      </div>
                    )}

                    {request.processed_at && (
                      <div className="text-sm text-gray-600">
                        <p>Processed by {request.processed_by} on {formatDate(request.processed_at)}</p>
                        {request.admin_notes && (
                          <div className="mt-1 p-2 bg-blue-50 rounded text-sm">
                            <strong>Admin Notes:</strong> {request.admin_notes}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {request.status === 'pending' && (
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => openProcessModal(request, 'approved')}
                      disabled={processingRequest === request._id}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                    >
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Approve
                    </button>
                    <button
                      onClick={() => openProcessModal(request, 'rejected')}
                      disabled={processingRequest === request._id}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Process Request Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center space-x-3 mb-4">
                {modalAction === 'approved' ? (
                  <CheckCircle className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircle className="h-6 w-6 text-red-500" />
                )}
                <h3 className="text-lg font-medium text-gray-900">
                  {modalAction === 'approved' ? 'Approve' : 'Reject'} Access Request
                </h3>
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">
                  <strong>User:</strong> {selectedRequest.name} ({selectedRequest.email})
                </p>
                {selectedRequest.message && (
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Request:</strong> {selectedRequest.message}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="adminNotes" className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes (Optional)
                </label>
                <textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder={`Add a note about why this request was ${modalAction}...`}
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => handleProcessRequest(selectedRequest._id, modalAction, adminNotes)}
                  disabled={processingRequest === selectedRequest._id}
                  className={`flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                    modalAction === 'approved'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {processingRequest === selectedRequest._id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    `${modalAction === 'approved' ? 'Approve' : 'Reject'} Request`
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedRequest(null);
                    setAdminNotes('');
                  }}
                  disabled={processingRequest === selectedRequest._id}
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 